import { ExitComponentProvider } from 'components/dataTables/agGridTable/PlanningTable/hooks/useExitComponentWithConfirmation';
import { useGlobalContext } from 'contexts/GlobalContext';
import { TRestriction } from 'contexts/models/globalContextModels';
import { useSimpleTabs } from 'contexts/SimpleTabsContext';
import { ICycle } from 'models/cycle/cycle.model';
import { createContext, useContext, useEffect, useMemo } from 'react';
import { useQuery } from 'services/hooks/useQuery';
import { useGetCycle } from 'services/queryClient/wrapperHooks/cycle/useGetCycle';

import MainTabs from '../components/tabs/MainTabs';
import { usePlanningFilterConfigurationSearch } from '../components/tabs/planningLevelConfiguration/components/planningFilterConfiguration/hooks/usePlanningFilterConfigurationSearch';
import {
	IContextPlanning,
	PlanningTypes,
	SCREEN_TYPE_CATEGORY,
	SCREEN_TYPE_TO_ENUM_MAPPING,
} from './types/contextPrevision.type';

const ContextPrevision = createContext<IContextPlanning>(
	{} as IContextPlanning
);

export function getScreenCategory(
	screenType: SCREEN_TYPE_TO_ENUM_MAPPING
): PlanningTypes {
	return SCREEN_TYPE_CATEGORY[screenType];
}

interface IProviderPlanning {
	screenType: SCREEN_TYPE_TO_ENUM_MAPPING;
}

/** Recurso em permissionsPage (/auth/permissoes). Planejamento usa collaborationView na rota — manter o mesmo aqui. */
const canEditDictionary: Record<SCREEN_TYPE_TO_ENUM_MAPPING, TRestriction> = {
	[SCREEN_TYPE_TO_ENUM_MAPPING.MARKETING_COLLABORATION]:
		'collaborationMarketing',
	[SCREEN_TYPE_TO_ENUM_MAPPING.PLANNING_COLLABORATION]:
		'collaborationView',
	[SCREEN_TYPE_TO_ENUM_MAPPING.COMMERCIAL_COLLABORATION]:
		'collaborationCommercial',
	[SCREEN_TYPE_TO_ENUM_MAPPING.CONSENSUS]: 'consensus',
};

export default function ProviderPlanning({ screenType }: IProviderPlanning) {
	const query = useQuery();
	const urlInitialCycleId = +(query.get('cycle-id') || '');
	const companyAreaId = +(query.get('company-area-id') || '');

	const { permissionsPage, userAttributes } = useGlobalContext();
	const { getCycleForContext, setSelectedCycleId, clearContextCycle } =
		useSimpleTabs();

	const contextCycleId = getCycleForContext(screenType, urlInitialCycleId);
	const effectiveCycleId = urlInitialCycleId || contextCycleId || 0;
	const validCycleId = effectiveCycleId > 0 ? effectiveCycleId : null;

	useEffect(() => {
		if (urlInitialCycleId) {
			setSelectedCycleId(urlInitialCycleId, screenType);
		} else if (!contextCycleId) {
			clearContextCycle(screenType);
		}
	}, [
		urlInitialCycleId,
		screenType,
		contextCycleId,
		setSelectedCycleId,
		clearContextCycle,
	]);

	const { data: cycle } = useGetCycle(validCycleId || undefined);

	const permissionKey = canEditDictionary[screenType];
	const pagePerm = permissionsPage[permissionKey];

	const getCanEdit = (cycleSelected?: ICycle) => {
		const cycleDeprecated = cycleSelected
			? cycleSelected?.is_deprecated
			: cycle?.is_deprecated;
		return !!pagePerm?.editar && !cycleDeprecated;
	};

	const getEditBlockReason = (
		cycleSelected?: ICycle
	): 'no_edit_permission' | 'deprecated_cycle' | null => {
		if (!pagePerm?.visualizar) return null;
		if (getCanEdit(cycleSelected)) return null;
		const cycleDeprecated = cycleSelected
			? cycleSelected?.is_deprecated
			: cycle?.is_deprecated;
		if (cycleDeprecated) return 'deprecated_cycle';
		if (!pagePerm?.editar) return 'no_edit_permission';
		return null;
	};

	const canView = !!pagePerm?.visualizar;

	const planningType = useMemo(
		() => getScreenCategory(screenType),
		[screenType]
	);

	const planningFilterConfigurationResult =
		usePlanningFilterConfigurationSearch(
			validCycleId || 0,
			userAttributes.email,
			planningType,
			companyAreaId ? companyAreaId : undefined
		);

	return (
		<ContextPrevision.Provider
			value={{
				cycleId: validCycleId || 0,
				screenType,
				companyAreaId,
				planningType,
				planningFilterConfigurationResult,
				cycle,
				getCanEdit,
				getEditBlockReason,
				canView,
			}}
		>
			<ExitComponentProvider>
				<MainTabs />
			</ExitComponentProvider>
		</ContextPrevision.Provider>
	);
}

export const useContextPrevision = () => {
	return useContext(ContextPrevision);
};
